import type { AuthUser } from '@/api/auth';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { anySkillEnabled, type AiSkills } from '@/lib/aiSkills';
import { computeHasFeature } from '@/stores/auth';

/**
 * Меню ученика в приложении — по разделам с сервера (D75, решение владельца 26.09.2026).
 *
 * Вкладка есть, только если её раздел пришёл в `sections` (`/auth/me`, ответ входа; старое имя `features`,
 * читать через `userSections`). Нет раздела — нет вкладки: без замков, «скоро», заглушек и флагов «false».
 * Главная и Профиль — всегда: без них в приложении не выйти из учётки и не открыть «Документы».
 *
 * ⚠️ БЕЗ НАБОРА — ТОЛЬКО ГЛАВНАЯ И ПРОФИЛЬ. На сайте отсутствие набора значит «показать всё» (безопасно к
 * блокировке, `computeHasFeature`), а в приложении — наоборот: разделы, которых сервер не назвал, ученик не
 * видит. Иначе ревьюер с Proxyman видел бы вкладки, которые отвечают 403.
 *
 * Так выходит: новичок (`lessons, vocab`) — Главная, Обучение, Словарь, Профиль (4, как в одобренном
 * приложении); платник с тренажёрами — ещё «Тренажёры» (5). Чата, расписания, живых уроков, разговорных
 * комнат и CEFR в приложении нет вовсе (решение владельца 26.09: без чата в 61) — вкладок для них нет,
 * сколько бы ключей ни прислал сервер. Сторож — `storeTabs.test.ts`.
 */

/** Ключи тренажёров: вкладка «Тренажёры» — если есть хоть один (как пункт меню сайта). */
export const STORE_TRAINER_KEYS = [
  'ielts_reading',
  'ielts_listening',
  'ielts_writing',
  'ielts_speaking',
  'sprint',
  'mock',
] as const;

/**
 * Спрашивать ли `GET /ielts/profile` (баллы мока) с главной и из профиля.
 *
 * ⚠️ В ПРИЛОЖЕНИИ — ТОЛЬКО ПРИ РАЗДЕЛЕ ТРЕНАЖЁРОВ (27.09.2026, Proxyman владельца перед подачей 61): у новичка
 * (`lessons, vocab`) главная звала мок и получала «no profile» — запрос к разделу, которого у ученика нет, тот же
 * класс, что чат (`canAskStudentChat`). Нет учётки или набора — не спрашиваем; сотрудник и `*` — как в
 * `computeHasFeature`. Сайт — как было, запрос всегда. Экраны тренажёров зовут мок сами, они под этим разделом.
 */
export function canAskIeltsProfile(user: AuthUser | null, app: boolean = IS_APP_BUILD): boolean {
  if (!app) return true;
  if (!user) return false;
  return STORE_TRAINER_KEYS.some((k) => computeHasFeature(user, k, true));
}

/**
 * Есть ли у ученика раздел тренажёров — ОДНО правило для вкладки, пункта меню, маршрутов `/app/trainers*` и ссылок
 * на тренажёры из урока и «Обучения» (02.10.2026, решение владельца к 1.1.1: бесплатник тренажёров не видит вообще).
 *
 * ⚠️ В ПРИЛОЖЕНИИ БЕЗ УЧЁТКИ И БЕЗ НАБОРА — НЕТ. `computeHasFeature` для незагруженной учётки отвечает «да» (не
 * прятать лишнего на сайте), и вход в тренажёры мелькнул бы у новичка до ответа сервера. Сайт — как у всех гейтов:
 * нет набора — открыто (безопасно к блокировке), сотрудник и `*` — открыто.
 */
export function hasTrainerSection(user: AuthUser | null, app: boolean = IS_APP_BUILD): boolean {
  if (app && !user) return false;
  return STORE_TRAINER_KEYS.some((k) => computeHasFeature(user, k, app));
}

type StoreTab = { to: string; keys?: readonly string[] };

/** Порядок вкладок внизу экрана. `keys` нет — вкладка есть всегда. */
const STORE_TABS: readonly StoreTab[] = [
  { to: '/app' },
  { to: '/app/lessons', keys: ['lessons'] },
  { to: '/app/trainers', keys: STORE_TRAINER_KEYS },
  { to: '/app/vocabulary', keys: ['vocab'] },
  { to: '/app/profile' },
];

/** Пути вкладок приложения для набора разделов ученика. `*` — все разделы. */
export function storeTabs(sections: readonly string[] | null | undefined): string[] {
  const has = (key: string) => !!sections && (sections.includes('*') || sections.includes(key));
  return STORE_TABS.filter((tab) => !tab.keys || tab.keys.some(has)).map((tab) => tab.to);
}

/**
 * Прятать ли вкладку «Тренажёры» по тумблерам ИИ-заданий (приложение 1.1.2, docs/CONTRACTS/ai-skills.md §1).
 *
 * Вкладка есть по разделам (`storeTabs`), но у платника может не быть ни одного включённого навыка: сервер или курс
 * выключили все четыре. Тогда внутри пусто, и вкладка прячется. Решение владельца 13.10.2026.
 *
 * ⚠️ ТОЛЬКО ПО ЗАГРУЖЕННОМУ ОТВЕТУ. До ответа и при ошибке ручки вкладка остаётся: спрятать её от сбоя сети значит
 * отнять у ученика раздел, который у него есть. Бесплатника это не касается вовсе — у него вкладки нет по разделам,
 * и ручку для него никто не зовёт (сторож `freeUserNoTrainers.test.ts`).
 */
export function trainersTabEmpty(loaded: boolean, failed: boolean, skills: AiSkills): boolean {
  return loaded && !failed && !anySkillEnabled(skills);
}
